import { castDate, dbDate } from "@caredna-marlin/utils/dates";
import { CommandIndexCareRequirementVisitsQuery, CommandIndexProvidersQuery } from "~/graphql/__generated__/graphql";
import { OrgConfig } from "~/providers/OrgConfigProvider";
import { LocalCalendarBlockBase, LocalShiftBase, LocalVisit, LocalVisitResource, LocalVisitResourceBase } from "~/hooks/visitDragDropReducer";
import { adjustOverlappingEvents, createVisitEventFromResource, CustomMbscCalendarEventData, getEventCalendarOrder, LocalVisitRequest } from "./eventsHelper";
import { buildCareRequirementWarningsByVisitResourceId, getVisitWarning } from "~/views/command/visitWarningUtils";
import { calendarBlockIsFilter } from "@caredna-marlin/utils/calendarBlockHelper";
import { isEqual } from "lodash";
import { startCase } from "lodash/fp";
import { filterProviders, FilterState } from "~/views/command/filters";
import { MbscResource } from "@mobiscroll/react";
import { UNASSIGNED_PROVIDER_ID, UNASSIGNED_PROVIDER_TYPE_ID } from "./visitsHelper";

export const beforeBuffer = (event: CustomMbscCalendarEventData) => {
  const visit = event.original!;
  const textColor = "black";
  if (visit.bufferBefore && visit.bufferBefore > 0) {
    return (
      <div
        className={`md-buffer md-before-buffer type-${event.original?.type}`}
        style={{
          background: "repeating-linear-gradient(-45deg, #f3f3f3, #f3f3f3 11px, #e5e5e5 11px, #e5e5e5 22px)",
          borderLeft: "1px solid #d7d7d7",
          borderTop: "1px solid #d7d7d7",
          borderBottom: "1px solid #d7d7d7",
        }}
      >
        <span className="md-buffer-time" style={{ color: textColor }}>
          {visit.bufferBefore} min
        </span>
      </div>
    );
  }
  return (<div></div>);
};

export const afterBuffer = (event: CustomMbscCalendarEventData) => {
  const visit = event.original!;
  const textColor = "black";
  if (visit.bufferAfter && visit.bufferAfter > 0) {
    return (
      <div
        className={`md-buffer md-after-buffer type-${event.original?.type}`}
        style={{
          background: "repeating-linear-gradient(-45deg, #f3f3f3, #f3f3f3 11px, #e5e5e5 11px, #e5e5e5 22px)",
          borderRight: "1px solid #d7d7d7",
          borderTop: "1px solid #d7d7d7",
          borderBottom: "1px solid #d7d7d7",
        }}
      >
        <span className="md-buffer-time" style={{ color: textColor }}>
          {visit.bufferAfter} min
        </span>
      </div>
    );
  }
  return (<div></div>);
};

export const visitsToCalendarEvents = (
  visibleVisits: LocalVisitResourceBase,
  localShifts: LocalShiftBase,
  localVisitResources: LocalVisitResource[],
  localVisits: LocalVisit[],
  localVisitRequests: LocalVisitRequest[],
  localCalendarBlocks: LocalCalendarBlockBase,
  careRequirementVisits: CommandIndexCareRequirementVisitsQuery["visit"],
  visibleProviders: CommandIndexProvidersQuery["commandIndexProviders"]["providers"],
  lastAddedVisitId: number | null,
  hasCreateDragEventsPermission: boolean,
  modifiedResourceIds: Set<number>,
  orgConfig: OrgConfig,
  isOptimizeReviewMode?: boolean,
  lockedVisitResourceIds?: Set<number>,
): CustomMbscCalendarEventData[] => {
  const careRequirementWarningsByVisitResourceId = buildCareRequirementWarningsByVisitResourceId(
    localVisitResources,
    careRequirementVisits
  );
  const baseEvents = visibleVisits.map((vr) => {
    const isLocked = lockedVisitResourceIds?.has(vr.id) ?? false;
    const event = createVisitEventFromResource(
      vr,
      lastAddedVisitId,
      hasCreateDragEventsPermission,
      modifiedResourceIds,
      isOptimizeReviewMode,
      isLocked,
      orgConfig,
    );

    return {
      ...event,
      tooltip: getVisitWarning(
        event,
        localShifts,
        localVisitResources,
        localVisits,
        localVisitRequests,
        localCalendarBlocks,
        visibleProviders,
        orgConfig,
        {},
        careRequirementWarningsByVisitResourceId
      ),
    }
  })
  return adjustOverlappingEvents(baseEvents);
};

export const shiftsToCalendarEvents = (visibleShifts: LocalShiftBase): CustomMbscCalendarEventData[] => {
  return visibleShifts.map((shift) => {
    return {
      id: shift.id,
      startDate: castDate(shift.startTime),
      endDate: castDate(shift.endTime),
      dbDate: dbDate(shift.startTime),
      date: castDate(shift.startTime).getTime(),
      resource: shift.provider?.id,
      providerId: shift.provider?.id ?? shift.providerId ?? null,
      tooltip: "",
      title: [shift.provider?.firstName, shift.provider?.lastName].join(" "),
      start: shift.startTime,
      end: shift.endTime,
      referenceId: shift.id,
      color: "gray",
      type: "shift" as const,
      order: getEventCalendarOrder('shift'),
      dragBetweenResources: false,
      dragInTime: false,
    };
  });
};

export const blocksToCalendarEvents = (visibleCalendarBlocks: LocalCalendarBlockBase, modifiedCalendarBlocks: Record<string, any>): CustomMbscCalendarEventData[] => {
  return visibleCalendarBlocks.map(calendarBlock => {
    const eventType = calendarBlockIsFilter(calendarBlock) ? "calendar_block_filter" as const : "calendar_block" as const;
    let cssClass = modifiedCalendarBlocks[calendarBlock.id] ? "dashed-border" : "";

    cssClass += `${calendarBlock.eventStatus === 'canceled' ? ' canceled-block' : ''}`;

    return {
      id: calendarBlock.id,
      referenceId: calendarBlock.id,
      start: calendarBlock.startTime,
      end: calendarBlock.endTime,
      date: castDate(calendarBlock.startTime).getTime(),
      startDate: castDate(calendarBlock.startTime),
      endDate: castDate(calendarBlock.endTime),
      dbDate: calendarBlock.startTime,
      resource: calendarBlock.providerId,
      providerId: calendarBlock.providerId,
      title: calendarBlock.title ?? "Block",
      tooltip: "",
      bufferBefore: (calendarBlock.startOfShiftDriveTime || calendarBlock.interVisitDriveTime)?.toString(),
      bufferAfter: calendarBlock.endOfShiftDriveTime?.toString(),
      type: eventType,
      order: getEventCalendarOrder(eventType),
      cssClass,
      dragBetweenResources: eventType === "calendar_block",
      dragInTime: eventType === "calendar_block",
      showEventTooltip: false,
    };
  });
};

export const providersToCalendarResources = (
  providersByType: CommandIndexProvidersQuery["commandIndexProviders"]["providersByType"],
  visibleShifts: LocalShiftBase,
  visibleVisits: LocalVisitResourceBase,
  visibleCalendarBlocks: LocalCalendarBlockBase,
  date: Date,
  collapsedGroups: Record<string, boolean>,
  filters: FilterState,
  currentProviderId: number | null,
): any[] => {
  const groups = providersByType
    .map((group) => {
      // Filter providers within this group
      const filteredProviders = filterProviders(group.providers, visibleShifts, visibleVisits, visibleCalendarBlocks, filters, currentProviderId);

      return {
        id: `type_${group.typeId}`,
        name: startCase(group.typeName),
        typeName: group.typeName,
        eventCreation: false,
        collapsed: collapsedGroups[group.typeId] ?? false,
        cssClass: 'mbsc-custom-provider-type-header',
        isParent: true,
        typeId: group.typeId,
        children: filteredProviders.map((provider) => ({
          id: provider.id,
          name: [provider.firstName, provider.lastName].join(" "),
          firstName: provider.firstName,
          lastName: provider.lastName,
          providerType: provider.providerType,
          active: provider.active,
          shift: visibleShifts.find(
            s =>
              s.provider?.id === provider.id &&
              s.startTime &&
              new Date(s.startTime).toDateString() === date.toDateString()
          ),
        })),
      };
    })
    .filter(group => group.children && group.children.length > 0);

  if (visibleVisits.find(v => v.providerId == null)) {
    // Always keep Unassigned group on top
    groups.unshift({
      id: `type_${UNASSIGNED_PROVIDER_TYPE_ID}`,
      name: "Unscheduled Visits",
      typeName: "TYPENAME",
      eventCreation: false,
      children: [{
          id: UNASSIGNED_PROVIDER_ID,
          name: "Unscheduled Visits",
          firstName: "Unscheduled",
          lastName: "Visits",
          providerType: null,
          active: true,
          shift: undefined,
        }],
      collapsed: false,
      cssClass: 'mbsc-custom-provider-type-header',
      isParent: true,
      typeId: UNASSIGNED_PROVIDER_TYPE_ID.toString(),
    })
  }

  return groups;
};

const childResourcesHaveChanged = (
  prevChildren: MbscResource[] | undefined,
  newChildren: MbscResource[] | undefined
): boolean => {
  if (prevChildren?.length !== newChildren?.length) {
    return true;
  }

  if (!prevChildren || !newChildren) {
    return false;
  }

  return !isEqual(
    prevChildren.map((child) => child.id),
    newChildren.map((child) => child.id)
  );
};

export const resourcesHaveChanged = (
  prevResources: MbscResource[],
  newResources: MbscResource[]
): boolean => {
  if (prevResources.length !== newResources.length) {
    return true;
  }

  for (let i = 0; i < prevResources.length; i++) {
    const prev = prevResources[i];
    const curr = newResources[i];

    if (prev.id !== curr.id ||
      prev.collapsed !== curr.collapsed ||
      prev.name !== curr.name ||
      prev.typeName !== curr.typeName ||
      childResourcesHaveChanged(prev.children, curr.children)
    ) {
      return true;
    }
  }
  return false;
};


export const eventsHaveChanged = (
  prevEvents: CustomMbscCalendarEventData[],
  newEvents: CustomMbscCalendarEventData[]
): boolean => {
  if (prevEvents.length !== newEvents.length) {
    return true;
  }

  if (prevEvents.length === 0) {
    return false;
  }

  // Compare each event for meaningful differences
  for (let i = 0; i < prevEvents.length; i++) {
    const prev = prevEvents[i];
    const curr = newEvents[i];

    // Quick checks first (most likely to differ)
    if (
      prev.id !== curr.id ||
      prev.resource !== curr.resource ||
      prev.start !== curr.start ||
      prev.end !== curr.end ||
      prev.startDate?.getTime() !== curr.startDate?.getTime() ||
      prev.endDate?.getTime() !== curr.endDate?.getTime()
    ) {
      return true;
    }

    // Visual changes Check status/color/toolip changes
    if (
      prev.color !== curr.color ||
      prev.cssClass !== curr.cssClass ||
      prev.title !== curr.title ||
      prev.tooltip !== curr.tooltip
    ) {
      return true;
    }

    // Check buffer times (drive time changes)
    if (
      prev.bufferBefore !== curr.bufferBefore ||
      prev.bufferAfter !== curr.bufferAfter
    ) {
      return true;
    }

    // Check original metadata for ghost events
    if (prev.original) {
      if (
        !curr.original ||
        prev.original.type !== curr.original.type ||
        prev.original.visitStatus !== curr.original.visitStatus ||
        prev.original.providerId !== curr.original.providerId
      ) {
        return true;
      }
    }
  }

  // No meaningful changes detected
  return false;
};
